import _ from 'lodash'
import { gql } from '@apollo/client'
import {
    fetchToleranceAnnotation,
    fetchThreadsAnnotation,
    fetchStandardToleranceAnnotation,
    fetchCertificateOfConformanceAnnotation,
    fetchMaterialCertificationAnnotation,
    fetchInspectionPointsAndRedactionBoxes,
} from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/DrawingAnalysisMenu/logic'
import { WebViewerInstance } from '@pdftron/webviewer'
import {
    INSPECTION_POINT_BUBBLE,
    REDACTION_BOXES,
    INSPECTION_POINT_SELECTION,
} from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/PreviewPanel/PreviewItem/PDFViewer/constants'

import { isSignedUriExpired } from '~/lib/assetUrlCache'
import {
    MAT_CATEGORY_ALUMINUM,
    MAT_CATEGORY_STAINLESS,
    MAT_CATEGORY_STEEL,
    MAT_CATEGORY_COPPER_BRASS_BRONZE,
    MAT_CATEGORY_PLASTIC,
    MAT_CATEGORY_MJF,
    MAT_CATEGORY_SLS,
    MAT_CATEGORY_POLYJET,
    MAT_CATEGORY_FDM,
    MAT_CATEGORY_SLA,
    MAT_CATEGORY_TITANIUM,
    MAT_CATEGORY_EXOTIC,
} from '~/lib/partsConstants'
import logger from '~/lib/logger'

export const isEmpty = (obj: any) =>
    [Object, Array].includes((obj || {}).constructor) &&
    !Object.entries(obj || {}).length

export function defered(func: Function, ...args: any) {
    if (typeof func !== 'function') {
        throw new TypeError('Expected a function')
    }
    return setTimeout(func, 1, ...args)
}

export function filterDFMFeedback(dfmFeedback: any, status: string) {
    return dfmFeedback?.filter((feed: any) => {
        return feed.severity === status
    })
}

export const getUrl = (pathname: string, selectedTab?: string) => {
    return pathname?.split(`/${selectedTab}`)[0]
}

export const isSigned2dUrlExpired = (item: any) => {
    // Check if any of the urls is expired - original, annotated, sanitized
    const oldDownload2dAnnotatedUrl =
        item?.attachmentLink?.quoteAttachmentRevision?.attachment?.annotated
            ?.downloadUrl

    if (
        oldDownload2dAnnotatedUrl &&
        isSignedUriExpired(oldDownload2dAnnotatedUrl)
    ) {
        return true
    }

    const oldDownload2dUrl =
        item?.attachmentLink?.quoteAttachmentRevision?.attachment?.downloadUrl

    if (oldDownload2dUrl && isSignedUriExpired(oldDownload2dUrl)) {
        return true
    }

    const oldDownload2dSanitizedUrl =
        item?.attachmentLink?.quoteAttachmentRevision?.attachment?.sanitized
            ?.downloadUrl

    if (
        oldDownload2dSanitizedUrl &&
        isSignedUriExpired(oldDownload2dSanitizedUrl)
    ) {
        return true
    }

    return false
}

export const isSigned3dUrlExpired = (url: any) => {
    if (!url) {
        return false
    }

    const isExpiredUrl = isSignedUriExpired(url)

    return isExpiredUrl
}

export const getUserAnnotationEnabled = gql`
    query queryIsDfmAnno($id: Int!) {
        user(id: $id) {
            id
            isDfmAnnotationsEnabled
        }
    }
`
interface MaterialCategoryMap {
    [key: string]: number | undefined
}

interface MaterialMap {
    id: string
    name: string
    materialCategory: string
    typename: string
}

const materialCategorySortingMap: MaterialCategoryMap = Object.freeze({
    // CNC Material Orderings
    [MAT_CATEGORY_ALUMINUM]: 1,
    [MAT_CATEGORY_STAINLESS]: 2,
    [MAT_CATEGORY_STEEL]: 3,
    [MAT_CATEGORY_COPPER_BRASS_BRONZE]: 4,
    [MAT_CATEGORY_PLASTIC]: 5,
    [MAT_CATEGORY_TITANIUM]: 6,
    [MAT_CATEGORY_EXOTIC]: 7,
    // 3DP Material Orderings
    [MAT_CATEGORY_MJF]: 1,
    [MAT_CATEGORY_SLS]: 2,
    [MAT_CATEGORY_POLYJET]: 3,
    [MAT_CATEGORY_FDM]: 4,
    [MAT_CATEGORY_SLA]: 5,
})

const BIG_INDEX = 1000

export function buildMaterialGroups(materials: MaterialMap[]) {
    const [withCategories, withoutCategories] = _.partition(
        materials,
        'materialCategory',
    )
    const withCategoriesSortedName = _.sortBy(withCategories, data =>
        data.name?.toLowerCase(),
    )
    const groupedMaterials = _.groupBy(
        withCategoriesSortedName,
        'materialCategory',
    )

    // Create an ordered array of material groups
    const materialGroups = _.map(groupedMaterials, (options, name) => {
        return { name, options }
    }).sort((a, b) => {
        const indexA = materialCategorySortingMap[a.name] || BIG_INDEX
        const indexB = materialCategorySortingMap[b.name] || BIG_INDEX

        return indexA - indexB
    })

    const extraMaterials = _.map(withoutCategories, material => {
        const { id, name } = material

        return { id, name }
    })

    return [...materialGroups, ...extraMaterials]
}

export const hideTolerancePoint = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const toleranceAnnotation = fetchToleranceAnnotation(allAnnotation) || []

    if (toleranceAnnotation.length && instance?.annotManager?.hideAnnotations) {
        instance.annotManager.hideAnnotations(toleranceAnnotation)
    }
}

export const hideRedactionBoxes = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const redactAnnotation =
        fetchInspectionPointsAndRedactionBoxes(
            allAnnotation,
            REDACTION_BOXES,
        ) || []

    if (redactAnnotation.length && instance?.annotManager?.hideAnnotations) {
        instance.annotManager.hideAnnotations(redactAnnotation)
    }
}

export const hideInspectionPointBubble = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const inspectionPointBubble =
        fetchInspectionPointsAndRedactionBoxes(
            allAnnotation,
            INSPECTION_POINT_BUBBLE,
        ) || []

    if (
        inspectionPointBubble.length &&
        instance?.annotManager?.hideAnnotations
    ) {
        instance.annotManager.hideAnnotations(inspectionPointBubble)
    }
}

export const hideInspectionPointSelection = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const inspectionPointSelection =
        fetchInspectionPointsAndRedactionBoxes(
            allAnnotation,
            INSPECTION_POINT_SELECTION,
        ) || []

    if (
        inspectionPointSelection.length &&
        instance?.annotManager?.hideAnnotations
    ) {
        instance.annotManager.hideAnnotations(inspectionPointSelection)
    }
}

export const showTolerancePoint = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const toleranceAnnotation = fetchToleranceAnnotation(allAnnotation) || []

    if (toleranceAnnotation.length && instance?.annotManager?.showAnnotations) {
        instance.annotManager.showAnnotations(toleranceAnnotation)
    }
}

export const toggleStandardTolerancePoints = ({
    webViewerInstance,
    display,
}: {
    webViewerInstance: WebViewerInstance
    display: boolean
}) => {
    const allAnnotation =
        webViewerInstance?.annotManager?.getAnnotationsList() || []

    const standardToleranceAnnotations =
        fetchStandardToleranceAnnotation(allAnnotation) || []

    if (
        standardToleranceAnnotations.length &&
        webViewerInstance?.annotManager?.showAnnotations &&
        display
    ) {
        webViewerInstance.annotManager.showAnnotations(
            standardToleranceAnnotations,
        )
    } else if (
        standardToleranceAnnotations.length &&
        webViewerInstance?.annotManager?.hideAnnotations &&
        !display
    ) {
        webViewerInstance.annotManager.hideAnnotations(
            standardToleranceAnnotations,
        )
    }
}
export const toggleCertificateOfConformance = ({
    webViewerInstance,
    display,
}: {
    webViewerInstance: WebViewerInstance
    display: boolean
}) => {
    const allAnnotation =
        webViewerInstance?.annotManager?.getAnnotationsList() || []

    const COCAnnotations =
        fetchCertificateOfConformanceAnnotation(allAnnotation) || []

    if (
        COCAnnotations.length &&
        webViewerInstance?.annotManager?.showAnnotations &&
        display
    ) {
        webViewerInstance.annotManager.showAnnotations(COCAnnotations)
    } else if (
        COCAnnotations.length &&
        webViewerInstance?.annotManager?.hideAnnotations &&
        !display
    ) {
        webViewerInstance.annotManager.hideAnnotations(COCAnnotations)
    }
}

export const toggleMaterialCertification = ({
    webViewerInstance,
    display,
}: {
    webViewerInstance: WebViewerInstance
    display: boolean
}) => {
    const allAnnotation =
        webViewerInstance?.annotManager?.getAnnotationsList() || []

    const MaterialCertificationAnnotations =
        fetchMaterialCertificationAnnotation(allAnnotation) || []

    if (
        MaterialCertificationAnnotations.length &&
        webViewerInstance?.annotManager?.showAnnotations &&
        display
    ) {
        webViewerInstance.annotManager.showAnnotations(
            MaterialCertificationAnnotations,
        )
    } else if (
        MaterialCertificationAnnotations.length &&
        webViewerInstance?.annotManager?.hideAnnotations &&
        !display
    ) {
        webViewerInstance.annotManager.hideAnnotations(
            MaterialCertificationAnnotations,
        )
    }
}
export const hideThreadsPoint = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const threadAnnotation = fetchThreadsAnnotation(allAnnotation) || []

    if (threadAnnotation.length && instance?.annotManager?.hideAnnotations) {
        instance.annotManager.hideAnnotations(threadAnnotation)
    }
}

export const showThreadsPoint = (instance: WebViewerInstance) => {
    const allAnnotation = instance?.annotManager?.getAnnotationsList() || []
    const threadAnnotation = fetchThreadsAnnotation(allAnnotation) || []

    if (threadAnnotation.length && instance?.annotManager?.showAnnotations) {
        instance.annotManager.showAnnotations(threadAnnotation)
    }
}

const unrecognizedXFDFTags = ['inspectionpointbubble']

const parserErrorElementTagName = 'parsererror'

export const isValidBase64Url = (base64String: string) => {
    const blobRegex =
        '(?:[A-Za-z0-9+\\/]{4})*(?:[A-Za-z0-9+\\/]{2}==|[A-Za-z0-9+/]{3}=)?'
    const mimeRegex = '(data:\\w+\\/[a-zA-Z\\+\\-\\.]+;base64,)'
    const regex = mimeRegex + blobRegex

    return new RegExp(`^${regex}$`, 'gi').test(base64String)
}

export const invalidInspectionPointImageData = (xfdfData: string) => {
    const domParser = new DOMParser()
    const xmlParsedData = domParser.parseFromString(xfdfData, 'text/xml')
    const xmlSerializer = new XMLSerializer()

    const xmlParsedString = xmlSerializer.serializeToString(xmlParsedData)

    const doc = domParser.parseFromString(xmlParsedString, 'text/xml')

    const inspectionPointBubbles = Array.from(
        doc.querySelectorAll('stamp[subject="InspectionPointBubble"]'),
    )

    const corruptInspectionPoint = []

    for (let index = 0; index < inspectionPointBubbles.length; index++) {
        const element = inspectionPointBubbles[index]

        const bubbleImageData =
            element.querySelector('imagedata')?.innerHTML || ''

        const customData = JSON.parse(
            element.querySelector('trn-custom-data')?.getAttribute('bytes') ||
                '{}',
        )

        const linkedInspectionPointSelectionImageData =
            doc.querySelector(
                `stamp[subject="InspectionPointSelection"][name="${customData.linkedId}"] imagedata`,
            )?.innerHTML || ''

        const isValidBubbleImageData = isValidBase64Url(bubbleImageData)
        const isValidSelectionImageData = isValidBase64Url(
            linkedInspectionPointSelectionImageData,
        )

        if (!isValidBubbleImageData || !isValidSelectionImageData) {
            corruptInspectionPoint.push(customData.index)
        }
    }

    return corruptInspectionPoint
}

export const isValidXfdf = (
    xfdfData: string,
    quoteId: string,
    qmrId: string,
) => {
    try {
        if (!xfdfData) {
            logger.exception('XFDF validation exception: empty xfdf data', {
                extra: {
                    variables: {
                        quoteId,
                        qmrId,
                    },
                },
            })

            return false
        }

        const domParser = new DOMParser()
        const xmlParsedData = domParser.parseFromString(xfdfData, 'text/xml')
        const xmlDocumentElement = xmlParsedData.documentElement
        const xmlSerializer = new XMLSerializer()

        const xmlParsedString = xmlSerializer.serializeToString(xmlParsedData)

        const doc = domParser.parseFromString(xmlParsedString, 'text/xml')

        const parseErrorElement = doc.getElementsByTagName(
            parserErrorElementTagName,
        )

        if (parseErrorElement.length > 0) {
            const errorMessages = Array.from(parseErrorElement)
                .map(element => element.innerHTML)
                .join('\n')

            logger.exception(
                'XFDF validation exception: error elements found in XML',
                {
                    extra: {
                        variables: {
                            quoteId,
                            qmrId,
                            parseErrorElement: errorMessages,
                        },
                    },
                },
            )
            return false
        }

        const nodes = [...xmlDocumentElement.querySelectorAll('*')]

        const allStamps = [...doc.getElementsByTagName('stamp')]

        const isAllStampHaveLinked = allStamps.every(stamp => {
            const stampCustomData =
                stamp.getElementsByTagName('trn-custom-data')

            const linkedId = JSON.parse(
                stampCustomData[0]?.getAttribute('bytes') || '{}',
            )?.linkedId

            let linkedStamp = doc.querySelector(`stamp[name='${linkedId}']`)

            if (!linkedStamp) {
                linkedStamp = doc.querySelector(`square[name='${linkedId}']`)
            }

            const stampData = JSON.parse(
                stampCustomData[0]?.getAttribute('bytes') || '{}',
            )

            if (
                stampData?.userName === 'StandardTolerance' ||
                stampData?.userName === 'CertificateOfConformance' ||
                stampData?.userName === 'materialCertification' ||
                stampData?.userName === 'Thread' ||
                stampData?.userName === 'Tolerance'
            ) {
                return true
            }

            return (
                linkedStamp &&
                linkedId &&
                linkedStamp?.parentElement?.tagName === 'annots'
            )
        })

        if (!isAllStampHaveLinked) {
            logger.exception(
                'XFDF validation exception: All stamp not linked',
                {
                    extra: {
                        variables: {
                            quoteId,
                            qmrId,
                        },
                    },
                },
            )
            return false
        }

        const invalidXFDFTagsFound = unrecognizedXFDFTags.filter(
            unrecognizedXFDFTag =>
                nodes.filter(node => {
                    return node.tagName === unrecognizedXFDFTag
                }).length > 0,
        )

        return invalidXFDFTagsFound.length === 0
    } catch (error) {
        logger.exception(error, {
            extra: {
                variables: {
                    quoteId,
                    qmrId,
                },
            },
        })
        return false
    }
}

export const checkPartRevisionshasDfmreports = <
    T extends { id: string; dfmReports: { isPublished: boolean }[] },
>(
    quoteModelRevisions: T[][],
    selectedQuoteModelRevisionId: string,
) => {
    const selectedQmr = quoteModelRevisions?.filter(item => {
        const selectQmrById = item?.some(
            qmrs => qmrs.id === selectedQuoteModelRevisionId,
        )

        if (selectQmrById) {
            return item
        }
    })

    const hasDfmReports = selectedQmr[0]?.filter(qmr => {
        if (qmr.dfmReports?.length > 0) {
            const publishedDfmReport = qmr.dfmReports.some(
                report => report.isPublished,
            )

            if (publishedDfmReport) {
                return qmr
            }
        }
    })

    return hasDfmReports?.length
}
